import React from 'react';

import useDataSource from 'hooks/useDataSource';
import { EDIT_GOALS, EDIT_LICENSE, EDIT_MATOMO, HIDE_ANALYTICS_BETA, SUPER_USER } from 'shared/constants/permissions';
import Loading from 'components/Loading';
import { Button, Result, Switch } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import useUser from 'hooks/useUser';
import { hasModuleLicense } from 'shared/utils/license';
import { Module } from 'shared/prisma/types';
import DashboardPage from 'components/Dashboard/DashboardPage';
import { Link, Redirect, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import Hero from 'components/Hero';
import { useLocalStorageState } from 'ahooks';
import theme from 'shared/constants/theme.json';
import LegacyAnalytics from './LegacyAnalytics/LegacyAnalytics';
import AnalyticsSettings from './LegacyAnalytics/AnalyticsSettings';
import VisitDimensionSettings from './LegacyAnalytics/VisitDimensionSettings';
import ActionDimensionSettings from './LegacyAnalytics/ActionDimensionSettings';
import ManageGoals from './LegacyAnalytics/ManageGoals';
import { ANALYTICS_VERSION_KEY, shouldUseLegacyAnalytics } from './analyticsNav';

const toggleStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '0 24px 12px',
  marginBottom: 12,
  borderBottom: `1px solid ${theme['border-color-base']}`,
};

const LegacyToggle = ({ checked, onChange }) => (
  <div style={toggleStyle}>
    <Switch
      size="small"
      checked={checked}
      onChange={onChange}
    />
    <span>Use new analytics</span>
  </div>
);

const analyticsSettingsRoutes = [
  {
    type: 'divider',
    dataSourcePermission: EDIT_MATOMO,
  },
  {
    path: 'settings',
    label: 'Settings',
    link: false,
    icon: <SettingOutlined />,
    dataSourcePermission: EDIT_MATOMO,
    children: [
      { path: 'tracking', label: 'Tracking', component: AnalyticsSettings },
      { path: 'visit-dimensions', label: 'Visit dimensions', component: VisitDimensionSettings },
      { path: 'action-dimensions', label: 'Action dimensions', component: ActionDimensionSettings },
      { path: 'goals', label: 'Goals', dataSourcePermission: EDIT_GOALS, component: ManageGoals },
    ],
  },
];

const AnalyticsDashboards = ({ beforeMenu }) => {
  const { isSuperUser } = useUser();
  const { dataSource, dataSourceId, loading, hasDataSourcePermission } = useDataSource();

  if (loading) {
    return <Loading />;
  }

  const hasAnalytics = hasModuleLicense(dataSource?.activeLicense, Module.Analytics);

  if (!isSuperUser && !hasAnalytics) {
    return (
      <Hero>
        <Result
          status="info"
          title="No license"
          subTitle={(
            <>
              <div>You need to have a license for Analytics to view this page.</div>
              <div>You can also check out our demo account for a preview of our products.</div>
            </>
          )}
          extra={(
            <>
              {hasDataSourcePermission(EDIT_LICENSE) && (
                <Link to={`/data-sources/${dataSourceId}/settings/license`}>
                  <Button type="primary">Upgrade license</Button>
                </Link>
              )}
              <Button type="primary" href="https://app.extellio.com/demo" target="_blank">Demo account</Button>
            </>
          )}
        />
      </Hero>
    );
  }

  return <DashboardPage dashboardPageKey="analyticsDashboardPageId" canEditParent beforeMenu={beforeMenu} persistKey="new" className="module-analytics" extraRoutes={analyticsSettingsRoutes} />;
};

export const RedirectAnalyticsNew = () => {
  const { pathname, search } = useLocation();
  return <Redirect to={`${pathname.replace('/analytics-new', '/analytics')}${search}`} />;
};

const Analytics = () => {
  const history = useHistory();
  const { url } = useRouteMatch();
  const { hasPermission } = useUser();
  const canSeeNew = hasPermission(SUPER_USER) || !hasPermission(HIDE_ANALYTICS_BETA);
  const [analyticsVersion, setAnalyticsVersion] = useLocalStorageState(ANALYTICS_VERSION_KEY, { defaultValue: 'new' });
  const useLegacy = shouldUseLegacyAnalytics(analyticsVersion, { canSeeNew });

  const beforeMenu = canSeeNew ? (
    <LegacyToggle
      checked={!useLegacy}
      onChange={(checked) => {
        setAnalyticsVersion(checked ? 'new' : 'legacy');
        history.replace(url);
      }}
    />
  ) : undefined;

  return useLegacy
    ? <LegacyAnalytics beforeMenu={beforeMenu} />
    : <AnalyticsDashboards beforeMenu={beforeMenu} />;
};

export default Analytics;
